import './styles.scss';

import { ChangeEvent, ComponentProps, FocusEvent, ReactElement, useEffect, useRef, useState } from 'react';

import Button from '@/common/Button';
import { getClasses } from '@/utils/strings';

export type ImageInputProps = ComponentProps<'div'> & {
  src?: string;
  uploadProgress?: number;
  error?: string;
  disabled?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const ImageInput = ({ src, onChange, children, disabled, ...props }: ImageInputProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [image, setImage] = useState<string | undefined>(src);
  const [uploadProgress, _setUploadProgress] = useState<number | undefined>(undefined);
  const [error, _setError] = useState<string | undefined>(undefined);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (disabled) return;
    const file = event.target.files?.[0];
    setImage(file ? URL.createObjectURL(file) : undefined);
    inputRef.current?.blur();
    onChange(event);
  };
  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    inputRef.current?.blur();
    props.onBlur?.(event);
  };

  const backgroundImage = image ? `url(${image})` : props.style?.backgroundImage || undefined;
  let backgroundColor = 'var(--bs-info)';
  if (uploadProgress === 100) {
    if (!error) backgroundColor = 'var(--bs-success)';
    else backgroundColor = 'var(--bs-danger)';
  }
  const styles = {
    ...(props.style ?? {}),
    backgroundImage,
  };

  useEffect(() => {
    if (src === image) return;
    setImage(src);
  }, [image, src]);

  return (
    <div {...props} className={getClasses('ImageInput', disabled && 'ImageInput-Disabled', props.className)}>
      {!!uploadProgress && (
        <div className="ImageInput-Progress" style={{ backgroundColor, height: `${uploadProgress}%` }}>
          {uploadProgress}%
        </div>
      )}
      <div className="ImageInput-Display" style={styles}>
        {!image && <div className="ImageInput-Placeholder">{children}</div>}
        <div className="ImageInput-Overlay">
          <input
            ref={inputRef}
            name="avatar"
            type="file"
            className="d-none"
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <Button className="ImageInput-Button" disabled={disabled} onClick={() => !disabled && inputRef.current?.click()}>
            <i className="fa fa-camera" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImageInput;
